import type { GameState } from '@pacmates/engine';

/** PART 2: score/death/level/round-over are canvas/HUD-only today -- a
 * screen-reader user hears nothing. Pure derivation off two consecutive
 * authoritative `GameState` snapshots (SPEC.md §1.1: no client-side event
 * bookkeeping), same idiom as `game/soundEvents.ts`'s `detectSoundEvents`
 * and `game/levelClear.ts`'s `didLevelClear` -- diff the two real
 * snapshots the server actually sent, never a client-guessed transition.
 *
 * Returns AT MOST ONE message per diff (the message queue for the live
 * region stays deliberately shallow -- "latest wins", never spam multiple
 * announcements for one tick) in priority order: round-over > level-up >
 * extra-life > your-death > score-milestone. `null` means nothing
 * announcement-worthy happened (including `prev === null`, the very first
 * snapshot -- nothing to diff against, so a mid-round join/reconnect never
 * spuriously announces). */

/** Every 1000 points crossed announces once -- NOT every dot (that would be
 * a firehose; a co-op round can pass hundreds of pellets a minute). */
const SCORE_MILESTONE_STEP = 1000;

export function computeAnnouncement(prev: GameState | null, cur: GameState, mySeat: 1 | 2): string | null {
  if (!prev) return null;

  if (!prev.gameOver && cur.gameOver) {
    return 'Round over';
  }

  if (cur.level > prev.level) {
    return `Level ${cur.level}`;
  }

  const idx = mySeat - 1;
  const me = cur.players[idx]!;
  const prevMe = prev.players[idx]!;

  if (me.lives > prevMe.lives) {
    return `Extra life! ${me.lives} ${me.lives === 1 ? 'life' : 'lives'} left`;
  }

  if (me.lives < prevMe.lives || me.deaths > prevMe.deaths) {
    return me.lives > 0
      ? `You lost a life. ${me.lives} ${me.lives === 1 ? 'life' : 'lives'} left`
      : 'You lost your last life';
  }

  const prevMilestone = Math.floor(prevMe.score / SCORE_MILESTONE_STEP);
  const curMilestone = Math.floor(me.score / SCORE_MILESTONE_STEP);
  if (curMilestone > prevMilestone) {
    return `Score ${(curMilestone * SCORE_MILESTONE_STEP).toLocaleString()} points`;
  }

  return null;
}
